/**
 * McardWindow — окно карточки площадки (ВБ, ОЗОН, Я.Маркет, Сайт) на каркасе сущности.
 *
 * Общее для всех четырёх: шапка, цепочка, полоса «Что дальше» со всеми
 * проверками сразу, колонка, единые вкладки (Обзор · Текст · Характеристики ·
 * Медиа · Цена и остатки · Склейка · История), режим чтения и правка через
 * журнал правок. Площадочные файлы сводят свою деталь к `McardModel` и
 * передают только то, что у площадки своё: блоки Обзора, вкладку «Медиа»,
 * содержимое «Цены и остатков».
 *
 * Окно открывается на чтение. «Править» (кнопка, клавиша E) делает редактируемыми
 * текст, характеристики, фото, а где площадка позволяет — бренд, габариты и
 * штрихкод; «Сохранить и отправить…» открывает подтверждение и отправляет
 * `window-edit` — правка ложится в ПИМ и журнал, а на площадку уходит фоновой
 * задачей. Цена не правится.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Copy,
  ExternalLink,
  FileText,
  History,
  Images,
  Layers,
  Link2,
  ListChecks,
  Pencil,
  Eye,
  RefreshCw,
  RotateCcw,
  Image as ImageIcon,
  ScrollText,
  Wallet,
} from 'lucide-react'
import { hasPermission } from '../../../../app/access'
import { Badge } from '../../../../components/ui/Badge'
import { Button } from '../../../../components/ui/Button'
import { EntityChain, type ChainItem } from '../../../../components/ui/EntityChain'
import { copyToClipboard } from '../../../../components/ui/copyToClipboard'
import { Lightbox } from '../../../../components/ui/Lightbox'
import { NextStepBar, type NextStepAction } from '../../../../components/ui/NextStepBar'
import { UnderlineTabs } from '../../../../components/ui/UnderlineTabs'
import type { IdMetaItem } from '../../../../components/ui/IdMeta'
import {
  EntityModalShell,
  type EntityMoreMenuItem,
} from '../../../../components/entity-view/EntityModalShell'
import { useEntityNavigator } from '../../../../components/entity-view/navigatorContext'
import { PhotoResendModal } from '../../../../components/marketplace/PhotoResendModal'
import type { PhotoResendInput } from '../../../../components/marketplace/photoResendApi'
import { useAuthStore } from '../../../../stores/authStore'
import { useToastStore } from '../../../../stores/toastStore'
import { UnsavedChangesDialog } from '../../../seo-v2/components/UnsavedChangesDialog'
import { isNoDesignCode } from '../../../products-v2/lib'
import { buildPresenceChain } from '../../../products-v2/presence'
import { MediaRibbon } from '../media-ribbon/MediaRibbon'
import { CardSidebar, type CardSidebarStat } from '../CardSidebar'
import { buildCharacteristicRows, countMissingRequired } from './characteristics'
import {
  managementMissingHint,
  NOT_CREATED_TITLE,
  NOT_LINKED_TITLE,
  buildMcardVerdict,
  diffFields,
  type McardPrimaryKind,
  type McardTab,
} from './checks'
import { Notice, Panel } from './McardParts'
import {
  BundleTab,
  CharacteristicsTab,
  HistoryTab,
  OverviewTab,
  PriceTab,
  ReadOnlyBanner,
  TextTab,
  type TextMode,
} from './McardTabs'
import {
  cardWindowLink,
  formatRub,
  PLATFORM_KIND,
  PLATFORM_NAME,
  pluralFields,
  type McardModel,
  type McardPlatform,
} from './model'
import { useMpCardEdit } from './useMpCardEdit'
import { useMpCardLink } from './useMpCardLink'
import { WindowEditDialogs, sendDisabledReason } from './WindowEditDialogs'

const NO_PERMISSIONS: string[] = []
const NEED_UPDATE_RIGHT = 'Нужно право «Править карточки» (Card:UPDATE)'
const RIBBON_SCOPES = [{ key: 'card', label: 'Эта карточка' }]
const LINKING_HREF = '/settings/marketplace-link'
const CARD_CHANGES_HREF = '/marketplace/card-changes'

export interface McardWindowProps {
  platform: McardPlatform
  sellerId: number | null
  sellerName?: string | null
  /** Ключ окна: ВБ — nmID, ОЗОН и Маркет — offer_id, Сайт — артикул. */
  windowKey: string
  /** null, пока ни строки списка, ни детали нет. */
  model: McardModel | null
  /** Деталь ещё не пришла: скелетоны вместо полосы, колонки и тела. */
  loading: boolean
  keys: string[]
  activeKey: string | null
  totalCount?: number
  onNavigate: (key: string) => void
  onClose: () => void
  canGoBack?: boolean
  onBack?: () => void
  /** «Сверить с площадкой»: ВБ/ОЗ опрашивают площадку, Маркет перечитывает копию. */
  onRefresh?: () => Promise<void> | void
  refreshing: boolean
  refreshLabel?: string
  refreshTitle?: string
  /** Синхронизация кабинета целиком (если страница её умеет). */
  onSync?: () => void
  /** Вкладка «Медиа» — редактор с перетаскиванием (ВБ, ОЗОН). */
  mediaEditable: boolean
  mediaHint?: React.ReactNode
  /** Блоки под редактором фото (видео ВБ, материалы ОЗОН). */
  mediaExtra?: React.ReactNode
  /** Своя вкладка «Медиа» (Маркет, Сайт). */
  renderMedia?: (ctx: { openLightbox: (index: number) => void }) => React.ReactNode
  /** Содержимое вкладки «Цена и остатки». */
  priceContent: React.ReactNode
  /** Площадочные блоки «Обзора» (претензии, публикация, SEO-канал). */
  overviewExtra?: React.ReactNode
  /** Связанные сущности (бывшая «Связи»). */
  relations?: React.ReactNode
  /** Номер склейки на площадке из детали. */
  bundleExternal?: { id: string | number | null | undefined; label: string }
  /** Дополнение вкладки «История» (у Сайта — история обмена). */
  historyExtra?: React.ReactNode
  /** Дополнение вкладки «Текст» (у Сайта — собранная запись). */
  textFooter?: React.ReactNode
  /** Правка из окна недоступна совсем (Сайт): причина и, возможно, действие. */
  readOnly?: { reason: string; action?: React.ReactNode }
  /** Действия полосы «Что дальше», которых нет у остальных площадок («Выгрузить на сайт»). */
  extraActions?: NextStepAction[]
  /** Действия из `extraActions` идут первыми (главными): у Сайта это «Выгрузить на сайт». */
  extraActionsFirst?: boolean
  extraMenu?: EntityMoreMenuItem[]
  /** Дополнительные цифры колонки (претензии ВБ). */
  extraStats?: CardSidebarStat[]
  /** Блок в колонке под идентификаторами. */
  sidebarFooter?: React.ReactNode
  /** Подсказка под полем «Фото» в колонке и подпись пустого кадра. */
  emptyImageText?: string
  /** Параметр ссылки на окно. */
  linkParam: 'nm' | 'offer' | 'article'
  /** Перечитать деталь после правки или сверки. */
  onReloadDetail: () => void
}

const KIND_ICON: Record<McardPlatform, typeof FileText> = {
  wb: FileText,
  ozon: FileText,
  ym: FileText,
  site: FileText,
}

export const McardWindow: React.FC<McardWindowProps> = (props) => {
  const {
    platform,
    sellerId,
    sellerName,
    windowKey,
    model,
    loading,
    keys,
    activeKey,
    totalCount,
    onNavigate,
    onClose,
    canGoBack,
    onBack,
    onRefresh,
    refreshing,
    refreshLabel = 'Сверить с площадкой',
    refreshTitle,
    onSync,
    mediaEditable,
    mediaHint,
    mediaExtra,
    renderMedia,
    priceContent,
    overviewExtra,
    relations,
    bundleExternal,
    historyExtra,
    textFooter,
    readOnly,
    extraActions,
    extraActionsFirst,
    extraMenu,
    extraStats,
    sidebarFooter,
    emptyImageText = 'На витрине нет фото',
    linkParam,
    onReloadDetail,
  } = props

  const entityNav = useEntityNavigator()
  const permissions = useAuthStore((state) => state.user?.permissions ?? NO_PERMISSIONS)
  const canUpdate = hasPermission('Card:UPDATE', permissions)
  const canOpenLinking = hasPermission('System:READ', permissions)

  const [tab, setTab] = useState<McardTab>('overview')
  const [textMode, setTextMode] = useState<TextMode>('showcase')
  const [charSearch, setCharSearch] = useState('')
  const [onlyMissing, setOnlyMissing] = useState(false)
  const [activeImage, setActiveImage] = useState(0)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [photoResendOpen, setPhotoResendOpen] = useState(false)
  const [unsavedOpen, setUnsavedOpen] = useState(false)
  const [historyTick, setHistoryTick] = useState(0)
  const pendingAction = useRef<null | (() => void)>(null)

  const link = useMpCardLink({ marketplace: platform, sellerId, article: model?.pimArticle ?? model?.article })
  // Связь с товаром/макетом и регистрация управления контентом кабинета — разные факты.
  const productKnown = Boolean(model?.pimArticle)
  // NODESIGN — служебный код товаров без макета: не номер, без ссылки.
  const noDesign = isNoDesignCode(model?.designCode)
  const designCode = noDesign ? null : (model?.designCode ?? null)
  const designKnown = Boolean(designCode)
  const [refreshingLocal, setRefreshingLocal] = useState(false)

  const afterEdit = useCallback(() => {
    link.reloadAll()
    onReloadDetail()
    setHistoryTick((value) => value + 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onReloadDetail, link.reloadAll])

  const edit = useMpCardEdit({
    sellerId,
    cardId: link.cardId,
    state: link.state,
    jobName: model?.article ?? windowKey,
    onDone: afterEdit,
  })

  // Смена карточки (стрелки, цепочка): вкладка, режимы и правка начинаются заново.
  const previousKey = useRef(windowKey)
  useEffect(() => {
    if (previousKey.current === windowKey) return
    previousKey.current = windowKey
    setTab('overview')
    setTextMode('showcase')
    setCharSearch('')
    setOnlyMissing(false)
    setActiveImage(0)
    setLightboxIndex(null)
    edit.cancel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowKey, sellerId])

  const hasUnsaved = edit.editing && edit.hasChanges
  const guard = useCallback(
    (action: () => void) => {
      if (!hasUnsaved) return action()
      pendingAction.current = action
      setUnsavedOpen(true)
      return undefined
    },
    [hasUnsaved],
  )
  const finishPending = () => {
    const action = pendingAction.current
    pendingAction.current = null
    if (action) action()
  }

  const showToast = useToastStore((state) => state.showToast)
  const copyText = async (text: string, what: string) => {
    const ok = await copyToClipboard(text)
    if (ok) showToast(`Скопировано: ${what}`, 'success')
    else showToast('Не удалось скопировать', 'error')
  }
  const openHref = (href: string) => window.open(href, '_blank', 'noopener,noreferrer')

  // ── Правка: можно ли ──────────────────────────────────────────────────────
  const editBlock: string | null = (() => {
    if (readOnly) return readOnly.reason
    if (link.link === 'loading') return 'Присутствие на площадках ещё загружается'
    if (link.link === 'error') return 'Не удалось определить связь с ПИМ'
    if (link.link === 'not_linked') return productKnown ? NOT_CREATED_TITLE : NOT_LINKED_TITLE
    if (link.stateLoading) return 'Состояние карточки в ПИМ ещё загружается'
    if (!link.state) return link.stateWhy ?? 'Состояние карточки в ПИМ недоступно'
    if (!link.state.editable) return link.state.editable_reason ?? 'Карточку нельзя править из окна'
    if (!canUpdate) return NEED_UPDATE_RIGHT
    return null
  })()

  // ── Модель и проверки ─────────────────────────────────────────────────────
  const missingRequired = model ? countMissingRequired(model.characteristics) : 0
  const verdict = useMemo(
    () =>
      model
        ? buildMcardVerdict({
            model,
            link: platform === 'site' ? 'linked' : link.link,
            windowState: link.state,
            windowStateLoading: link.stateLoading,
            windowStateWhy: link.stateWhy,
            missingRequired,
            productKnown,
            designKnown,
          })
        : null,
    [link.link, link.state, link.stateLoading, link.stateWhy, missingRequired, model, platform, productKnown, designKnown],
  )
  const charRows = useMemo(
    () => (model ? buildCharacteristicRows(model.characteristics, link.state) : []),
    [link.state, model],
  )
  const diff = diffFields(link.state, model?.characteristics)

  const photos = model?.photos ?? []
  // Ссылка на витрину: из детали, иначе из зеркала присутствия (у Маркета и Сайта детали её не несут).
  const platformUrl = model?.platformUrl ?? link.entry?.mirror?.url ?? null
  const viewPhotos = edit.editing && mediaEditable ? edit.draft.media : photos
  const ribbonBaseline = link.state?.fields.media_urls.value
  const platformPhotos = link.state?.fields.media_urls.platform_value
  const ribbonRemote = Array.isArray(platformPhotos) ? platformPhotos : undefined

  const photoResendInput: PhotoResendInput | null = model
    ? platform === 'site'
      ? { articles: [model.article] }
      : sellerId !== null
        ? { cards: [{ article: model.article, marketplace: platform, seller_id: sellerId }] }
        : null
    : null
  const photoResendTitle =
    platform === 'site'
      ? `Обновить фото артикула ${model?.article ?? ''} из ПИМ (все площадки)`
      : `Обновить фото из ПИМ · ${model?.article ?? ''}`
  const photoResendBlock = !canUpdate ? NEED_UPDATE_RIGHT : !photoResendInput ? 'Не указан кабинет карточки' : undefined

  const doRefresh = async () => {
    if (!onRefresh) return
    setRefreshingLocal(true)
    try {
      await onRefresh()
      link.reloadState()
    } finally {
      setRefreshingLocal(false)
    }
  }
  const refreshBusy = refreshing || refreshingLocal

  // ── Полоса «Что дальше» ───────────────────────────────────────────────────
  const openPlatform = () => platformUrl && openHref(platformUrl)
  const primaryAction = (kind: McardPrimaryKind): NextStepAction | null => {
    switch (kind) {
      case 'open_platform':
        return platformUrl
          ? { key: 'open', label: 'Открыть на площадке', icon: <ExternalLink className="h-4 w-4" />, onClick: openPlatform }
          : null
      case 'show_issues':
        return { key: 'issues', label: 'Показать ошибки', onClick: () => setTab('overview') }
      case 'show_missing':
        return {
          key: 'missing',
          label: 'Показать незаполненные',
          onClick: () => {
            setOnlyMissing(true)
            setTab('characteristics')
          },
        }
      case 'show_diff':
        return {
          key: 'diff',
          label: 'Показать разницу',
          onClick: () => {
            setTextMode('diff')
            setTab('text')
          },
        }
      case 'refresh_photos':
        return {
          key: 'photos',
          label: 'Обновить фото из ПИМ…',
          icon: <ImageIcon className="h-4 w-4" />,
          disabledReason: photoResendBlock,
          onClick: () => setPhotoResendOpen(true),
        }
      case 'link_pim':
        return {
          key: 'link',
          label: 'Открыть раздел связывания',
          icon: <Link2 className="h-4 w-4" />,
          disabledReason: canOpenLinking ? undefined : 'Раздел связывания доступен администратору',
          onClick: () => openHref(LINKING_HREF),
        }
      default:
        return null
    }
  }
  const nextActions: NextStepAction[] = []
  if (verdict) {
    const main = primaryAction(verdict.primary)
    if (extraActionsFirst) {
      for (const extra of extraActions ?? []) nextActions.push({ ...extra, variant: nextActions.length ? 'secondary' : 'primary' })
      if (main) nextActions.push({ ...main, variant: nextActions.length ? 'secondary' : 'primary' })
    } else {
      if (main) nextActions.push(main)
      for (const extra of extraActions ?? []) nextActions.push({ ...extra, variant: nextActions.length ? 'secondary' : 'primary' })
    }
    if (verdict.primary !== 'refresh_photos' && nextActions.length < 3) {
      const photosAction = primaryAction('refresh_photos')
      if (photosAction) nextActions.push({ ...photosAction, variant: 'secondary' })
    }
    if (verdict.primary !== 'open_platform' && nextActions.length < 3) {
      const open = primaryAction('open_platform')
      if (open) nextActions.push({ ...open, variant: 'secondary' })
    }
  }

  const tabAttention = (key: McardTab): 'warn' | 'bad' | undefined => {
    if (!model || loading) return undefined
    const errors = model.issues.filter((issue) => issue.level === 'error').length
    const warnings = model.issues.filter((issue) => issue.level === 'warning').length
    switch (key) {
      case 'overview':
        return errors > 0 || model.showcase.tone === 'bad' ? 'bad' : warnings > 0 ? 'warn' : undefined
      case 'text':
        return diff.length > 0 ? 'warn' : undefined
      case 'characteristics':
        return missingRequired > 0 ? 'bad' : undefined
      case 'media':
        return photos.length === 0 ? 'bad' : undefined
      default:
        return undefined
    }
  }

  const tabDefs: Array<{ key: McardTab; label: string; icon: React.ReactNode; count?: number }> = [
    { key: 'overview', label: 'Обзор', icon: <Eye className="h-4 w-4" /> },
    { key: 'text', label: 'Текст', icon: <FileText className="h-4 w-4" /> },
    { key: 'characteristics', label: 'Характеристики', icon: <ListChecks className="h-4 w-4" />, count: charRows.length || undefined },
    { key: 'media', label: 'Медиа', icon: <Images className="h-4 w-4" />, count: photos.length || undefined },
    { key: 'price', label: 'Цена и остатки', icon: <Wallet className="h-4 w-4" /> },
    { key: 'bundle', label: 'Склейка', icon: <Layers className="h-4 w-4" /> },
    { key: 'history', label: 'История', icon: <History className="h-4 w-4" /> },
  ]

  // ── Шапка ─────────────────────────────────────────────────────────────────
  const article = model?.article ?? ''
  const cabinetLabel = model?.cabinetLabel || sellerName || (sellerId !== null ? `#${sellerId}` : '')
  const metaItems: IdMetaItem[] = model
    ? [
        ...model.idMeta,
        ...(noDesign ? [{ label: 'Макет', value: 'Без макета' }] : []),
        { label: 'Кабинет', value: cabinetLabel },
        { label: 'Штрихкод', value: model.barcode, mono: true, copy: true },
      ]
    : []
  const sendOff = link.state
    ? sendDisabledReason(platform, link.state, { text: true })
    : null
  const errorsCount = model?.issues.filter((issue) => issue.level === 'error').length ?? 0

  const badges = model ? (
    <>
      <Badge variant={model.statusVariant} dot>
        {model.statusLabel}
      </Badge>
      {diff.length > 0 && (
        <Badge variant="yellow" dot>
          Расходится с ПИМ: {diff.length} {pluralFields(diff.length)}
        </Badge>
      )}
      {errorsCount > 0 && <Badge variant="red">Ошибок: {errorsCount}</Badge>}
      {sendOff && (
        <span title={sendOff}>
          <Badge variant="gray">Отправка правок выключена</Badge>
        </span>
      )}
    </>
  ) : undefined

  const platformLink = platformUrl
  const toolbar = platformLink ? (
    <a
      href={platformLink}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 hover:no-underline dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
    >
      <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
      Открыть на площадке
    </a>
  ) : undefined

  // ── Меню «Ещё» ────────────────────────────────────────────────────────────
  const moreMenu: EntityMoreMenuItem[] = [
    {
      key: 'open-platform',
      label: 'Открыть на площадке',
      icon: <ExternalLink className="h-4 w-4" />,
      disabledReason: platformLink ? undefined : 'Ссылки на витрину нет',
      onClick: openPlatform,
    },
    {
      key: 'copy-showcase-link',
      label: 'Скопировать ссылку на витрину',
      icon: <Link2 className="h-4 w-4" />,
      disabledReason: platformLink ? undefined : 'Ссылки на витрину нет',
      onClick: () => platformLink && void copyText(platformLink, 'ссылка на витрину'),
    },
    {
      key: 'copy-article',
      label: 'Скопировать артикул',
      icon: <Copy className="h-4 w-4" />,
      hotkey: 'C',
      onClick: () => void copyText(article, 'артикул'),
    },
    { divider: true },
    ...(onRefresh
      ? [{
          key: 'refresh',
          label: refreshLabel,
          icon: <RefreshCw className="h-4 w-4" />,
          disabledReason: refreshBusy ? 'Сверка уже идёт' : undefined,
          onClick: () => void doRefresh(),
        }]
      : []),
    {
      key: 'photo-resend',
      label: 'Обновить фото из ПИМ…',
      icon: <ImageIcon className="h-4 w-4" />,
      disabledReason: photoResendBlock,
      onClick: () => setPhotoResendOpen(true),
    },
    {
      key: 'journal',
      label: 'Журнал изменений карточки',
      icon: <ScrollText className="h-4 w-4" />,
      onClick: () => {
        const params = new URLSearchParams({ mode: 'sends', article })
        if (platform !== 'site') params.set('marketplace', platform)
        if (sellerId !== null) params.set('seller_id', String(sellerId))
        openHref(`${CARD_CHANGES_HREF}?${params.toString()}`)
      },
    },
    ...(onSync
      ? [{
          key: 'sync-cabinet',
          label: 'Синхронизировать кабинет',
          icon: <RotateCcw className="h-4 w-4" />,
          onClick: onSync,
        }]
      : []),
    ...(extraMenu && extraMenu.length ? [{ divider: true as const }, ...extraMenu] : []),
  ]

  // ── Цепочка ───────────────────────────────────────────────────────────────
  const pimCard = link.entry?.pim_card ?? null
  const chainItems: ChainItem[] = [
    designCode
      ? {
          key: 'design',
          label: 'Макет',
          sub: `#${designCode}`,
          tone: 'ok',
          onClick: entityNav.available
            ? () => guard(() => entityNav.open({ kind: 'design', code: designCode }))
            : undefined,
        }
      : noDesign
        ? { key: 'design', label: 'Без макета', tone: 'none', disabled: true, title: 'у товара нет макета' }
        : { key: 'design', label: 'Макет', tone: 'none', disabled: true, title: 'макет карточки не определён' },
    model?.pimArticle
      ? {
          key: 'product',
          label: 'Товар',
          sub: model.pimArticle,
          tone: 'ok',
          onClick: entityNav.available
            ? () => guard(() => entityNav.open({ kind: 'product', article: model.pimArticle as string }))
            : undefined,
        }
      : { key: 'product', label: 'Товар', tone: 'none', disabled: true, title: 'карточка не связана с товаром ПИМ' },
    ...(platform !== 'site'
      ? [
          pimCard && sellerId !== null
            ? {
                key: 'draft',
                label: 'Черновик',
                sub: `#${pimCard.id}`,
                tone: 'ok' as const,
                onClick: entityNav.available
                  ? () =>
                      guard(() =>
                        entityNav.open({ kind: 'draft', cardId: pimCard.id, sellerId, marketplace: platform as 'wb' | 'ozon' | 'ym' }),
                      )
                  : undefined,
              }
            : {
                key: 'draft',
                label: 'Черновик',
                tone: 'none' as const,
                disabled: true,
                title: 'карточки ПИМ у этой карточки площадки нет',
              },
        ]
      : []),
    buildPresenceChain({
      entries: link.presence.entries,
      article,
      open: entityNav.available ? entityNav.open : undefined,
      current: { marketplace: platform, sellerId },
      guard,
    }),
  ]

  // ── Колонка ───────────────────────────────────────────────────────────────
  const errorCount = errorsCount
  const warningCount = model?.issues.filter((issue) => issue.level === 'warning').length ?? 0
  const stats: CardSidebarStat[] = model
    ? [
        {
          label: 'Цена на витрине',
          wide: true,
          value:
            model.price.shown !== null ? (
              <>
                {formatRub(model.price.shown)}
                {Number(model.price.old) > Number(model.price.shown) && (
                  <span className="ml-1.5 text-xs font-normal text-gray-400 line-through dark:text-zinc-500">
                    {formatRub(model.price.old)}
                  </span>
                )}
              </>
            ) : (
              'нет данных'
            ),
          tone: model.price.shown !== null ? undefined : 'dim',
          note:
            Number(model.price.discountPercent) > 0
              ? `скидка ${model.price.discountPercent} %`
              : model.price.shown === null
                ? 'площадка не прислала цену'
                : undefined,
        },
        model.stock !== null
          ? { label: 'Остаток', value: `${model.stock.toLocaleString('ru-RU')} шт.`, note: 'на складах' }
          : { label: 'Остаток', value: 'нет данных', tone: 'dim', note: model.stockWhy || 'нет источника' },
        {
          label: 'Фото',
          value: photos.length || 'нет',
          tone: photos.length ? undefined : 'bad',
          // «на витрине» только если карточка на витрине; иначе это фото в карточке, которую ещё не выгрузили
          note: (model.showcase.onShowcase ?? !model.showcase.problem)
            ? photos.length ? 'на витрине' : 'нет на витрине'
            : photos.length ? 'в карточке' : 'нет в карточке',
        },
        {
          label: 'Ошибки',
          value: errorCount || 'нет',
          tone: errorCount ? 'bad' : 'dim',
          note: warningCount
            ? `ещё ${warningCount} предупр.`
            : errorCount
              ? 'мешают обновлению'
              : undefined,
        },
        {
          label: 'Характеристики',
          value: charRows.length || 'нет',
          tone: charRows.length ? undefined : 'dim',
          note: charRows.length ? (missingRequired ? `не хватает ${missingRequired}` : 'заполнено') : 'нет данных',
        },
        ...(extraStats ?? []),
      ]
    : []

  const openProduct = (target: Parameters<typeof entityNav.open>[0]) => {
    if (entityNav.available) guard(() => entityNav.open(target))
  }

  const sidebar = model ? (
    <CardSidebar
      images={photos}
      activeImage={Math.min(activeImage, Math.max(photos.length - 1, 0))}
      onActiveImage={setActiveImage}
      onOpenImage={setLightboxIndex}
      // «+N» в ленте миниатюр ведёт на вкладку «Медиа».
      onShowAll={() => setTab('media')}
      emptyImageText={emptyImageText}
      stats={stats}
      origins={[
        {
          label: 'Товар PIM',
          value: model.pimArticle || 'нет связи',
          mono: true,
          onClick: model.pimArticle && entityNav.available ? () => openProduct({ kind: 'product', article: model.pimArticle as string }) : undefined,
          href: model.pimArticle && !entityNav.available ? `/products?article=${encodeURIComponent(model.pimArticle)}` : undefined,
        },
        {
          label: 'Макет',
          value: noDesign ? 'Без макета' : designCode ? `№ ${designCode}` : '—',
          mono: !noDesign,
          onClick: designCode && entityNav.available ? () => openProduct({ kind: 'design', code: designCode }) : undefined,
          href: designCode && !entityNav.available ? `/designs?design=${encodeURIComponent(designCode)}` : undefined,
        },
        {
          label: 'Тематика',
          value: model.themeNumber ? `№ ${model.themeNumber}` : '—',
          mono: true,
        },
      ]}
      footer={sidebarFooter}
    />
  ) : (
    // Колонка есть всегда: пока данных нет, каркас рисует на её месте скелетон.
    <span />
  )

  // ── Футер ─────────────────────────────────────────────────────────────────
  const footer = (
    <>
      <div className="flex min-w-0 items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
        {edit.editing ? (
          <>
            <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500" />
            <span className="truncate text-amber-700 dark:text-amber-400">
              Режим правки · Изменено: {edit.dirtyLabels.length ? edit.dirtyLabels.join(', ') : 'ничего'}
            </span>
          </>
        ) : (
          <span>
            {model?.lastSyncLabel ?? 'Сверена с площадкой'}{' '}
            {model?.lastSyncAt ? new Date(model.lastSyncAt).toLocaleString('ru-RU') : 'нет данных'}
          </span>
        )}
      </div>
      <div className="ml-auto flex items-center gap-2">
        {onRefresh && (
          <Button
            size="sm"
            variant="secondary"
            icon={<RefreshCw className="h-3.5 w-3.5" />}
            loading={refreshBusy}
            title={refreshTitle}
            onClick={() => void doRefresh()}
          >
            {refreshLabel}
          </Button>
        )}
        {edit.editing ? (
          <>
            <Button size="sm" variant="secondary" onClick={edit.cancel}>
              Отменить
            </Button>
            <Button size="sm" disabled={!edit.hasChanges} onClick={edit.openConfirm}>
              Сохранить и отправить…
            </Button>
          </>
        ) : (
          <span title={editBlock ?? 'Править (E)'}>
            <Button
              size="sm"
              variant="secondary"
              icon={<Pencil className="h-3.5 w-3.5" />}
              disabled={Boolean(editBlock)}
              onClick={edit.start}
            >
              Править
            </Button>
          </span>
        )}
      </div>
    </>
  )

  // ── Баннер «только чтение» ────────────────────────────────────────────────
  const structuralBlock: string | null = readOnly
    ? readOnly.reason
    : link.link === 'not_linked'
      ? productKnown
        ? managementMissingHint(designKnown)
        : 'Карточка не связана с ПИМ: править её из окна нельзя.'
      : link.state && !link.state.editable
        ? (link.state.editable_reason ?? 'Карточку нельзя править из окна.')
        : null
  const banner =
    structuralBlock && !loading && !(verdict?.primary === 'link_pim' && !readOnly && link.link === 'not_linked') ? (
      <ReadOnlyBanner
        reason={structuralBlock}
        action={
          !readOnly && link.link === 'not_linked' ? (
            canOpenLinking ? (
              <a href={LINKING_HREF} className="shrink-0 font-medium underline">
                Открыть раздел связывания
              </a>
            ) : (
              <span className="shrink-0">Связывание доступно администратору</span>
            )
          ) : (
            readOnly?.action
          )
        }
      />
    ) : undefined

  // ── Вкладка «Медиа» ───────────────────────────────────────────────────────
  const mediaDiffers =
    link.state && link.state.fields.media_urls.overridden
      ? link.state.fields.media_urls.value.join('|') !== link.state.fields.media_urls.platform_value?.join('|')
      : false
  const defaultMedia = (
    <div className="space-y-4">
      <Panel
        title="Фотографии · порядок отправки"
        aside={
          <Button
            size="sm"
            variant="secondary"
            icon={<ImageIcon className="h-3.5 w-3.5" />}
            disabled={Boolean(photoResendBlock)}
            title={photoResendBlock}
            onClick={() => setPhotoResendOpen(true)}
          >
            Обновить фото из ПИМ…
          </Button>
        }
      >
        {mediaDiffers && !edit.editing && (
          <div className="mb-3">
            <Notice tone="warn">
              В ПИМ набор фото отличается от витрины: правка сохранена, но на площадку ещё не ушла.
            </Notice>
          </div>
        )}
        <MediaRibbon
          urls={viewPhotos}
          onChange={edit.setMedia}
          article={article}
          cabinet={link.entry?.cabinet_code ?? link.state?.card.cabinet ?? null}
          maxCount={30}
          disabled={!edit.editing}
          onOpen={(index) => setLightboxIndex(index)}
          hint={mediaHint}
          aspect={platform === 'wb' || platform === 'ozon' ? '3:4' : '1:1'}
          // Отметки расхождения — только в режиме правки: в чтении набор и есть витрина.
          baselineUrls={edit.editing ? ribbonBaseline : undefined}
          // «Сравнить с площадкой»: что на витрине сейчас. Визуалы кабинета ПИМ window-state не отдаёт.
          remoteUrls={edit.editing ? ribbonRemote : undefined}
          scopes={RIBBON_SCOPES}
          scope="card"
          affectedCards={1}
        />
      </Panel>
      {mediaExtra}
    </div>
  )
  const mediaContent = renderMedia ? (
    <div className="space-y-4">
      {renderMedia({ openLightbox: setLightboxIndex })}
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="secondary"
          icon={<ImageIcon className="h-3.5 w-3.5" />}
          disabled={Boolean(photoResendBlock)}
          title={photoResendBlock}
          onClick={() => setPhotoResendOpen(true)}
        >
          Обновить фото из ПИМ…
        </Button>
      </div>
    </div>
  ) : (
    defaultMedia
  )

  const hotkeys = { keys: tabDefs.map((item) => item.key), onSelect: (key: string) => setTab(key as McardTab) }
  const suspend =
    lightboxIndex != null ||
    unsavedOpen ||
    photoResendOpen ||
    edit.phase === 'confirm' ||
    edit.phase === 'saving' ||
    edit.phase === 'running' ||
    edit.phase === 'done' ||
    edit.phase === 'failed'

  return (
    <>
      <EntityModalShell
        isOpen
        ariaLabel={`${PLATFORM_KIND[platform]} ${article || windowKey}`}
        keys={keys}
        activeKey={activeKey}
        totalCount={totalCount}
        onNavigate={(key) => guard(() => onNavigate(key))}
        onClose={() => guard(onClose)}
        canGoBack={canGoBack}
        onBack={onBack ? () => guard(onBack) : undefined}
        suspendHotkeys={suspend}
        loading={loading}
        kind={{ label: PLATFORM_KIND[platform], icon: KIND_ICON[platform] }}
        primaryId={{ value: article || windowKey, label: 'артикул', link: cardWindowLink(linkParam, windowKey) }}
        badges={badges}
        title={model?.title || 'Без названия'}
        meta={metaItems}
        toolbar={toolbar}
        moreMenu={moreMenu}
        chain={
          <EntityChain
            items={chainItems}
            onBack={canGoBack && onBack ? () => guard(onBack) : undefined}
          />
        }
        banner={banner}
        nextStep={
          verdict ? (
            <NextStepBar
              tone={verdict.tone}
              title={verdict.title}
              hint={verdict.hint}
              actions={nextActions}
              checks={verdict.checks.map((check) => ({
                key: check.key,
                label: check.label,
                value: check.value,
                tone: check.tone,
                title: check.title,
                onClick: () => {
                  if (check.key === 'pim' && diff.length > 0) setTextMode('diff')
                  setTab(check.tab)
                },
              }))}
            />
          ) : undefined
        }
        tabsHotkeys={hotkeys}
        onSave={() => {
          if (edit.editing && edit.hasChanges) edit.openConfirm()
        }}
        onToggleEdit={() => {
          if (!edit.editing && !editBlock) edit.start()
          else if (edit.editing && !edit.hasChanges) edit.cancel()
        }}
        tabs={
          <UnderlineTabs<McardTab>
            ariaLabel="Разделы карточки"
            active={tab}
            onChange={setTab}
            tabs={tabDefs.map((item) => ({
              key: item.key,
              label: item.label,
              icon: item.icon,
              count: item.count,
              attention: tabAttention(item.key),
            }))}
          />
        }
        sidebar={sidebar}
        footer={footer}
      >
        {model && tab === 'overview' && (
          <OverviewTab
            model={model}
            presenceEntries={link.presence.entries}
            currentSellerId={sellerId}
            canOpenOther={entityNav.available}
            onOpenOther={(target) => guard(() => entityNav.open(target))}
            extra={overviewExtra}
            relations={relations}
          />
        )}
        {model && tab === 'text' && (
          <TextTab
            model={model}
            state={link.state}
            edit={edit}
            mode={textMode}
            onMode={setTextMode}
            readOnlyReason={editBlock}
            footer={textFooter}
          />
        )}
        {model && tab === 'characteristics' && (
          <CharacteristicsTab
            platformName={PLATFORM_NAME[platform]}
            rows={charRows}
            state={link.state}
            edit={edit}
            specsKnown={model.specsKnown}
            search={charSearch}
            onSearch={setCharSearch}
            onlyMissing={onlyMissing}
            onOnlyMissing={setOnlyMissing}
          />
        )}
        {model && tab === 'media' && mediaContent}
        {model && tab === 'price' && <PriceTab platform={platform}>{priceContent}</PriceTab>}
        {model && tab === 'bundle' && (
          <BundleTab
            platform={platform}
            sellerId={sellerId}
            article={article}
            externalId={bundleExternal?.id}
            externalLabel={bundleExternal?.label}
            active
          />
        )}
        {model && tab === 'history' && (
          <HistoryTab
            platform={platform}
            sellerId={sellerId}
            article={article}
            state={link.state}
            lastSyncAt={model.lastSyncAt}
            active
            extra={historyExtra}
            reloadKey={historyTick}
          />
        )}
      </EntityModalShell>

      <Lightbox
        isOpen={lightboxIndex != null}
        items={viewPhotos.map((src, index) => ({ src, label: `Фото ${index + 1}` }))}
        index={lightboxIndex || 0}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />

      <WindowEditDialogs
        edit={{
          ...edit,
          finish: () => {
            const succeeded = edit.phase === 'done'
            edit.finish()
            if (succeeded) finishPending()
            else pendingAction.current = null
          },
        }}
        platform={platform}
        cabinet={cabinetLabel}
        state={link.state}
      />

      <UnsavedChangesDialog
        isOpen={unsavedOpen}
        saving={edit.phase === 'saving'}
        dirtyLabels={edit.dirtyLabels}
        onCancel={() => {
          setUnsavedOpen(false)
          pendingAction.current = null
        }}
        onDiscard={() => {
          setUnsavedOpen(false)
          edit.cancel()
          finishPending()
        }}
        onSaveAndContinue={() => {
          setUnsavedOpen(false)
          edit.openConfirm()
        }}
      />

      <PhotoResendModal
        isOpen={photoResendOpen}
        onClose={() => setPhotoResendOpen(false)}
        input={photoResendInput}
        title={photoResendTitle}
      />
    </>
  )
}

export default McardWindow
